import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { formatDate } from '../utils/dateFormat';
import '../styles/AdminDashboard.css';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentProducts, setRecentProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/admin/dashboard');
      setStats(response.data.stats);
      setRecentProducts(response.data.recentProducts || []);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des données');
      setLoading(false);
      if (err.response?.status === 403) {
        navigate('/');
      }
    }
  };

  if (loading) return <div className="loading">{t('common.loading')}</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>{t('admin.dashboard.title')}</h1>
      </div>

      {/* Statistiques */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>{t('admin.dashboard.users')}</h3>
          <p className="stat-number">{stats?.totalUsers || 0}</p>
        </div>
        <div className="stat-card">
          <h3>{t('admin.dashboard.products')}</h3>
          <p className="stat-number">{stats?.totalProducts || 0}</p>
        </div>
        <div className="stat-card">
          <h3>{t('admin.dashboard.pending')}</h3>
          <p className="stat-number">{stats?.pendingProducts || 0}</p>
        </div>
        <div className="stat-card">
          <h3>{t('admin.dashboard.active')}</h3>
          <p className="stat-number">{stats?.activeProducts || 0}</p>
        </div>
        <div className="stat-card revenue">
          <h3>{t('admin.dashboard.totalRevenue')}</h3>
          <p className="stat-number">{stats?.totalRevenue || 0} CHF</p>
        </div>
      </div>

      {/* Alertes commandes */}
      {(stats?.openDisputes > 0 || stats?.overdueOrders > 0) && (
        <div className="orders-alerts">
          {stats?.openDisputes > 0 && (
            <div className="alert-card alert-dispute" onClick={() => navigate('/admin/orders')}>
              <div className="alert-count">{stats.openDisputes}</div>
              <div className="alert-text">
                <strong>{t('admin.dashboard.openDisputes')}</strong>
                <span>{t('admin.dashboard.needsAttention')}</span>
              </div>
            </div>
          )}
          {stats?.overdueOrders > 0 && (
            <div className="alert-card alert-overdue" onClick={() => navigate('/admin/orders')}>
              <div className="alert-count">{stats.overdueOrders}</div>
              <div className="alert-text">
                <strong>{t('admin.dashboard.overdueOrders')}</strong>
                <span>{t('admin.dashboard.overdueDesc')}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Actions rapides */}
      <div className="quick-actions">
        <h2>{t('admin.dashboard.quickActions')}</h2>
        <div className="action-buttons">
          <button onClick={() => navigate('/admin/users')} className="action-btn">
            {t('admin.dashboard.manageUsers')}
          </button>
          <button onClick={() => navigate('/admin/products')} className="action-btn">
            {t('admin.dashboard.manageProducts')}
          </button>
          <button onClick={() => navigate('/admin/orders')} className="action-btn action-btn-orders">
            {t('admin.dashboard.manageOrders')}
          </button>
          <button onClick={() => navigate('/admin/theme')} className="action-btn">
            {t('admin.dashboard.customizeTheme')}
          </button>
        </div>
      </div>

      {/* Produits récents */}
      <div className="recent-products">
        <h2>{t('admin.dashboard.recentProducts')}</h2>
        {recentProducts.length === 0 ? (
          <p>{t('admin.dashboard.noRecentProducts')}</p>
        ) : (
          <div className="products-table">
            <table>
              <thead>
                <tr>
                  <th>{t('common.title')}</th>
                  <th>{t('common.seller')}</th>
                  <th>{t('common.date')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('common.price')}</th>
                </tr>
              </thead>
              <tbody>
                {recentProducts.map(product => (
                  <tr key={product.id}>
                    <td>{product.title}</td>
                    <td>{product.username}</td>
                    <td>{formatDate(product.created_at)}</td>
                    <td>
                      <span className={`status-badge ${product.status}`}>
                        {product.status}
                      </span>
                    </td>
                    <td>{product.price} CHF</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
